import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { withLocale } from "~/lib/utils/with-locale";
import { useForm } from "react-hook-form";
import z from "zod";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  authClient,
  createAuthError,
  localizeAuthErrorMessage,
} from "~/lib/better-auth/client";
import { ResetPasswordSchema } from "~/lib/zod-schemas/auth.schema";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "~/components/ui/form";
import { CheckCircle, LoaderIcon, TriangleAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";

type ResetPasswordSearch = {
  token: string;
  error?: string;
};

function parseResetPasswordSearch(
  search: Record<string, unknown>,
): ResetPasswordSearch {
  return {
    token: (search.token as string) || "",
    error: typeof search.error === "string" ? search.error : undefined,
  };
}

export const Route = createFileRoute("/_login/reset-password")({
  validateSearch: parseResetPasswordSearch,
  component: ResetPasswordPage,
});

/** Reset-password form page. Exported for use under __main/$locale/reset-password. */
export function ResetPasswordPage() {
  const { token, error: searchError } = useSearch({ strict: false }) as ResetPasswordSearch;
  const queryClient = useQueryClient();
  const hasInvalidToken = searchError === "INVALID_TOKEN" || !token;

  const form = useForm<z.infer<typeof ResetPasswordSchema>>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      password: "",
      passwordRepeat: "",
    },
  });

  const resetPassword = async (data: ResetPasswordSchema) => {
    const { data: response, error } = await authClient.resetPassword({
      newPassword: data.password,
      token,
    });
    if (error) {
      throw createAuthError(
        error,
        "Beim Zurücksetzen des Passworts ist ein Fehler aufgetreten. Bitte versuche es erneut.",
        "password-reset",
      );
    }
    return response;
  };

  const resetPasswordMutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      queryClient.resetQueries();
    },
  });
  const { isPending: isLoading, error, isSuccess } = resetPasswordMutation;

  const handleSubmit = async (values: z.infer<typeof ResetPasswordSchema>) => {
    try {
      await resetPasswordMutation.mutateAsync(values);
      form.reset();
    } catch (err) {
      console.error("Caught mutation error:", err);
    }
  };

  return (
    <div className="flex flex-col items-center mt-24">
      <h1 className="mt-6 font-medium text-5xl">Passwort zurücksetzen</h1>
      <div className="w-[480px]">
        {isSuccess && (
          <Alert className="mt-6">
            <CheckCircle />
            <AlertTitle>Zurücksetzen erfolgreich</AlertTitle>
            <AlertDescription>
              Das Zurücksetzen war erfolgreich. Logge dich jetzt ein.{" "}
              <Link className="underline" to={withLocale("/sign-in")}>
                Zum Login
              </Link>
            </AlertDescription>
          </Alert>
        )}
        {hasInvalidToken && !isSuccess && (
          <Alert className="mt-6" variant={"destructive"}>
            <TriangleAlert />
            <AlertTitle>Ungültiger Link</AlertTitle>
            <AlertDescription>
              Der Link zum Zurücksetzen ist ungültig oder abgelaufen. Bitte{" "}
              <Link className="underline" to={withLocale("/forgot-password")}>
                fordere einen neuen Link an
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}
        {error && (
          <Alert className="mt-6" variant={"destructive"}>
            <TriangleAlert />
            <AlertTitle>Fehler beim Zurücksetzen</AlertTitle>
            <AlertDescription>
              {localizeAuthErrorMessage(error.message)}
            </AlertDescription>
          </Alert>
        )}
        {!hasInvalidToken && !isSuccess && (
          <div className="w-[480px]">
            <div className="mt-8">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="flex flex-col gap-3"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="mt-1">
                            <Input
                              {...field}
                              id="password"
                              name="password"
                              type="password"
                              maxLength={100}
                              placeholder="Passwort"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="passwordRepeat"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="mt-1">
                            <Input
                              {...field}
                              id="password-repeat"
                              name="password-repeat"
                              type="password"
                              maxLength={100}
                              placeholder="Passwort wiederholen"
                            />
                          </div>
                        </FormControl>
                        <FormMessage className="text-red-500" />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" variant="default">
                    {isLoading ? (
                      <>
                        <LoaderIcon className="animate-spin" />
                        Lädt...
                      </>
                    ) : (
                      "Bestätigen"
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
